import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, inject } from "@angular/core";
import { hasSelectedPlan } from "../../utils/selected-plan";
import { Router } from "@angular/router";
import {
  DashboardLayoutComponent as ZDashboardLayoutComponent,
  DropdownMenuComponent,
  EDashboardLayout,
  IconComponent,
  IThemeService,
  themeServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { IMenuItem, MenuItemCommand, MenuItemRouterLink } from "@zaeper/core-lib";
import { sessionStateServiceInjectionToken } from "../../global-states/session/session-state-service-injection-token";
import { ISessionStateService } from "../../global-states/session/i-session-state.service";
import { Observable, Subscription, take } from "rxjs";
import { ICurrentUser } from "../../models/i-current-user";
import { ICloud } from "../../models/i-cloud";
import { IGroup } from "../../models/i-group";
import { languageStateServiceInjectionToken } from "../../services/language/languageStateServiceInjectionToken";
import { ILanguageStateService } from "@zaeper/localization-lib";
import { IReceiver } from "../../models/i-receiver";

import { ButtonModule } from "primeng/button";
import { MenuModule } from "primeng/menu";
import { ListboxModule } from "primeng/listbox";
import { BadgeModule } from "primeng/badge";
import { AvatarModule } from "primeng/avatar";
import { SpeedDialModule } from "primeng/speeddial";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import {
  ReceiverQuickAccessMenuComponent
} from "../../menus/receiver-quick-access-menu/receiver-quick-access-menu.component";
import { GroupQuickAccessMenuComponent } from "../../menus/group-quick-access-menu/group-quick-access-menu.component";
import { CloudQuickAccessMenuComponent } from "../../menus/cloud-quick-access-menu/cloud-quick-access-menu.component";
import { UserMenuComponent } from "../../menus/user-menu/user-menu.component";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import {
  welcomeWizardModalServiceInjectionToken
} from "../../modals/welcome-wizard-modal/welcome-wizard-modal-service-injection-token";
import { IWelcomeWizardModalService } from "../../modals/welcome-wizard-modal/i-welcome-wizard-modal.service";
import {
  infoModalServiceInjectionToken
} from "../../modals/info-modal/info-modal-service-injection-token";
import { IInfoModalService } from "../../modals/info-modal/i-info-modal.service";
import {
  appSwitcherModalServiceInjectionToken
} from "../../modals/app-switcher-modal/app-switcher-modal-service-injection-token";
import { AppSwitcherModalService } from "../../modals/app-switcher-modal/app-switcher-modal.service";
import { IAppSwitcherModalService } from "../../modals/app-switcher-modal/i-app-switcher-modal.service";

@Component( {
  selector: "app-dashboard-layout",
  templateUrl: "./dashboard-layout.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ZDashboardLayoutComponent,
    ButtonModule,
    MenuModule,
    ListboxModule,
    BadgeModule,
    AvatarModule,
    SpeedDialModule,
    InputTextModule,
    TooltipModule,
    ReceiverQuickAccessMenuComponent,
    GroupQuickAccessMenuComponent,
    CloudQuickAccessMenuComponent,
    UserMenuComponent,
    ZTemplateDirective
],
  providers: [
    {
      provide: appSwitcherModalServiceInjectionToken,
      useClass: AppSwitcherModalService
    }
  ]
} )
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  @Input( "routerPath" ) public routerPath: string = "";

  public branding: string = 'assets/images/zaeper_logo_z.svg';
  public readonly dashboardLayout: EDashboardLayout = EDashboardLayout.BLOCKS;
  public readonly currentUser$: Observable<ICurrentUser | null>;
  public readonly activeCloud$: Observable<ICloud | null>;
  public readonly activeGroup$: Observable<IGroup | null>;
  public readonly activeReceiver$: Observable<IReceiver | null>;
  public readonly language$: Observable<string>;

  public readonly navItems: IMenuItem[] = [
    new MenuItemRouterLink( {
      icon: "tv_outline",
      title: "receiver",
      internalLink: `/de/`
    } ), new MenuItemRouterLink( {
      icon: "storefront",
      title: "store",
      internalLink: `/de/store`
    } ), new MenuItemRouterLink( {
      icon: "star_outlined",
      title: "features",
      internalLink: `/de/features`
    } ), new MenuItemCommand( {
      icon: "info_outline",
      title: "infos",
      command: () => {
        this._infoModalService.open();
      }
    } )
  ];

  private readonly _subscriptions: Subscription = new Subscription();
  private readonly _themeService: IThemeService = inject( themeServiceInjectionToken );

  constructor(
    @Inject( sessionStateServiceInjectionToken ) private readonly _sessionStateService: ISessionStateService,
    @Inject( languageStateServiceInjectionToken ) private readonly _languageStateService: ILanguageStateService,
    @Inject( welcomeWizardModalServiceInjectionToken ) private readonly _welcomeWizardModalService: IWelcomeWizardModalService,
    @Inject( infoModalServiceInjectionToken ) private readonly _infoModalService: IInfoModalService,
    @Inject( appSwitcherModalServiceInjectionToken ) private readonly _appSwitcherModalService: IAppSwitcherModalService,
    private readonly _router: Router,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.currentUser$ = this._sessionStateService.currentUser$;
    this.activeCloud$ = this._sessionStateService.activeCloud$;
    this.activeGroup$ = this._sessionStateService.activeGroup$;
    this.activeReceiver$ = this._sessionStateService.activeReceiver$;
    this.language$ = this._languageStateService.language$;
  }

  public openAppSwitcher(): void {
    this._appSwitcherModalService.open();
  }

  private _updateBranding(isDark: boolean): void {
    this.branding = isDark
      ? 'assets/images/zaeper_logo_z_white.svg'
      : 'assets/images/zaeper_logo_z.svg';
    this._cdr.markForCheck();
  }

  ngOnInit(): void {
    this._subscriptions.add(
      this._themeService.isDarkMode$.subscribe( isDark => this._updateBranding( isDark ) )
    );
    this._sessionStateService.activeCompany$.pipe(take(1)).subscribe(company => {
      if (!company) {
        const pendingRequest = this._sessionStateService.getPendingJoinRequest();
        const initialState = pendingRequest ? "pending" : "choose";
        const dialogRef = this._welcomeWizardModalService.open(initialState);
        dialogRef.onClose.subscribe((result: boolean) => {
          if (result) {
            // A plan from the website's calculator opens on the features page, preselected.
            const nextUrl = hasSelectedPlan() ? "/de/features" : this._router.url;
            this._router.navigateByUrl("/", { skipLocationChange: true })
              .then(() => this._router.navigateByUrl(nextUrl));
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
